import { session } from '../api.ts';

/**
 * Browser capture → evidence ingest.
 *
 * The answer to "someone records an incident on their phone and it becomes
 * court-grade evidence" without an Eagle in their hand. A signed-in dashboard
 * user records through the browser (getUserMedia / MediaRecorder), the bytes
 * are hashed **in the browser** as they are produced, and the recording is
 * ingested into the evidence service through the exact contract a device meets
 * (evac-evidence docs/spec/03-platform/02-evidence-core.md §1.1–1.2).
 *
 * **The honesty decision — this is `declared`, not `captured`.**
 *
 * A device grades `captured`: it signs the manifest with a key held in a secure
 * element, and the platform refuses a signature-less `captured_by: device`
 * manifest rather than silently grading it up (spec §1.1, §14 A5). A browser
 * has no secure element and no attestable key, so a browser recording cannot
 * honestly claim device attestation. It is instead a **third-party upload with
 * a provenance manifest** (spec §1.5): `captured_by: 'third_party'`,
 * `signatures: []`, `provenance_grade: 'declared'`, and a provenance block the
 * uploading user completes. The verification statement such an asset can carry
 * is exactly "integrity guaranteed from ingest at T, provenance as declared by
 * X" — never more (spec §1.5). That is the truthful grade, and every downstream
 * custody artefact surfaces it verbatim.
 *
 * In one line: **browser capture is provable-from-ingest, not
 * device-attested.** The hashes still bind every byte — a defendant with the
 * media can re-hash it against the Merkle root — but the chain begins the
 * moment the platform first saw the bytes, not at a trusted sensor.
 *
 * NOTE (future dedupe): the EVAC→evidence token exchange replicated here
 * (`grantEvidenceToken`) is the same one `web/src/lib/evidence.ts` owns in the
 * open PR #224. When that lands, this should import its exported `grant`
 * helper rather than carry its own copy of the endpoint + auth.
 */

/* -------------------------------------------------------------- config */

// Duplicated from api.ts (BASE is not exported there). Folds into the shared
// evidence client when PR #224 merges — see the note above.
const EVAC_BASE = import.meta.env.VITE_API_URL ?? 'https://api.staging.evac.duress.com';

/**
 * Default segment size. A recording is written as segments, each carrying its
 * own SHA-256; the asset hash is the Merkle root over them, so a partially
 * transferred recording stays verifiable segment-by-segment and a lost segment
 * is *provable* rather than silent (spec §1.1). The device default is
 * 300 s / keyframe-aligned; here, with no keyframe access from MediaRecorder,
 * the boundary is a byte threshold. It is configurable per fleet profile in the
 * device contract (§1.1), so the capture surface exposes it too.
 */
export const SEGMENT_BYTES = 8 * 1024 * 1024;

/* -------------------------------------------------------------- errors */

export type CaptureStage = 'grant' | 'opening' | 'uploading' | 'completing' | 'reason_required';

/**
 * A failure the capture UI can turn into an honest, recoverable state. `stage`
 * says which hop broke, so the screen can say "upload failed — retry" rather
 * than a bare stack trace.
 */
export class CaptureError extends Error {
  constructor(
    readonly stage: CaptureStage,
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'CaptureError';
  }
}

/* --------------------------------------------------------------- hashes */

const LEAF_PREFIX = new Uint8Array([0x00]);
const NODE_PREFIX = new Uint8Array([0x01]);

function concat(parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

async function digest(...parts: Uint8Array[]): Promise<Uint8Array> {
  const bytes = concat(parts);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return new Uint8Array(hash);
}

export function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

export function hexToBytes(hex: string): Uint8Array {
  if (!/^[0-9a-f]*$/.test(hex) || hex.length % 2 !== 0) {
    throw new Error('not lowercase hex');
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Base64 of a SHA-256 hex digest — the value S3 wants in `x-amz-checksum-sha256`. */
export function hexToBase64(hex: string): string {
  return bytesToBase64(hexToBytes(hex));
}

/** Streaming per-segment hash, computed in the browser (spec §1.1). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  return bytesToHex(await digest(bytes));
}

/** Largest power of two strictly smaller than n (n >= 2). */
function split(n: number): number {
  let k = 1;
  while (k * 2 < n) k *= 2;
  return k;
}

async function mth(leaves: Uint8Array[]): Promise<Uint8Array> {
  if (leaves.length === 1) return digest(LEAF_PREFIX, leaves[0] as Uint8Array);
  const k = split(leaves.length);
  const [left, right] = await Promise.all([mth(leaves.slice(0, k)), mth(leaves.slice(k))]);
  return digest(NODE_PREFIX, left, right);
}

/**
 * RFC 6962 Merkle root over segment SHA-256 hashes, as lowercase hex — the
 * asset hash the ingest service recomputes over registered per-part hashes at
 * completion and compares to the manifest root (spec §1.2). Ported from
 * evac-evidence-service `src/evidence/merkle.ts`; the domain-separation
 * prefixes (0x00 leaf, 0x01 node) are the whole point, so a subtree can never
 * be grafted in as a segment.
 *
 * Empty input is refused: an asset with zero segments is not an asset.
 */
export async function merkleRootHex(segmentHashesHex: string[]): Promise<string> {
  if (segmentHashesHex.length === 0) {
    throw new Error('merkle root over zero segments is undefined for an asset');
  }
  const leaves = segmentHashesHex.map((hex) => {
    if (!/^[0-9a-f]{64}$/.test(hex)) throw new Error('segment hash must be 64 lowercase hex chars');
    return hexToBytes(hex);
  });
  return bytesToHex(await mth(leaves));
}

/* --------------------------------------------------------- segmenting */

export interface CaptureSegment {
  index: number;
  bytes: Uint8Array;
  sha256: string;
}

/**
 * Accumulates MediaRecorder chunks into segments, hashing each as it is sealed.
 *
 * MediaRecorder emits opaque chunks on its timeslice; those are gathered until
 * they cross the byte threshold, then sealed into a segment with its own
 * SHA-256. `flush()` seals whatever remains when recording stops. Hashing at
 * seal time is what makes this "streaming during capture, never post-hoc over a
 * closed file" (spec §1.1) — the last segment is hashed the instant recording
 * ends, not by re-reading a finished blob.
 */
export class SegmentAccumulator {
  private buffered: Uint8Array[] = [];
  private bufferedBytes = 0;
  private nextIndex = 0;
  readonly segments: CaptureSegment[] = [];

  constructor(private readonly threshold = SEGMENT_BYTES) {}

  async push(chunk: Uint8Array): Promise<CaptureSegment | null> {
    if (chunk.length === 0) return null;
    this.buffered.push(chunk);
    this.bufferedBytes += chunk.length;
    if (this.bufferedBytes >= this.threshold) return this.seal();
    return null;
  }

  async flush(): Promise<CaptureSegment | null> {
    if (this.bufferedBytes === 0) return null;
    return this.seal();
  }

  private async seal(): Promise<CaptureSegment> {
    const bytes = concat(this.buffered);
    this.buffered = [];
    this.bufferedBytes = 0;
    const segment: CaptureSegment = {
      index: this.nextIndex,
      bytes,
      sha256: await sha256Hex(bytes),
    };
    this.nextIndex += 1;
    this.segments.push(segment);
    return segment;
  }
}

/** `web-<short-random>` — the device_id a browser recording carries (task §1). */
export function webDeviceId(): string {
  const rand = new Uint8Array(4);
  crypto.getRandomValues(rand);
  return `web-${bytesToHex(rand)}`;
}

/* --------------------------------------------------------- manifest */

export interface Provenance {
  source_description: string;
  original_custodian: string;
  how_obtained: string;
  declared_by: string;
}

export interface CaptureManifest {
  manifest_version: 1;
  content: {
    media_type: string;
    bytes: number;
    duration_s?: number;
    segments: { index: number; bytes: number; sha256: string }[];
    merkle_root: string;
  };
  capture: {
    // 'third_party', never 'device': see the honesty decision at the top of
    // this file. A browser cannot attest capture, so it does not claim to.
    captured_by: 'third_party';
    device_id: string;
    started_at: string;
    ended_at: string;
  };
  provenance_grade: 'declared';
  // Empty, and honestly so: there is no secure element to sign with. A device
  // manifest carries a capture signature here; a browser one carries none, and
  // the grade reflects that rather than papering over it.
  signatures: [];
}

export interface CaptureMeta {
  mediaType: string;
  startedAt: string;
  endedAt: string;
  deviceId: string;
  /** The signed-in user, recorded as the declaring custodian. */
  custodian: string;
  durationSeconds?: number;
}

export function buildProvenance(custodian: string): Provenance {
  return {
    source_description: 'In-browser capture',
    original_custodian: custodian,
    how_obtained: 'Recorded in the Duress dashboard',
    declared_by: custodian,
  };
}

/** Assemble the capture manifest (spec §2.1 shape), computing the Merkle root. */
export async function assembleManifest(
  segments: CaptureSegment[],
  meta: CaptureMeta,
): Promise<CaptureManifest> {
  if (segments.length === 0) throw new Error('cannot build a manifest with no segments');
  const merkleRoot = await merkleRootHex(segments.map((s) => s.sha256));
  return {
    manifest_version: 1,
    content: {
      media_type: meta.mediaType,
      bytes: segments.reduce((n, s) => n + s.bytes.length, 0),
      ...(meta.durationSeconds != null ? { duration_s: meta.durationSeconds } : {}),
      segments: segments.map((s) => ({ index: s.index, bytes: s.bytes.length, sha256: s.sha256 })),
      merkle_root: merkleRoot,
    },
    capture: {
      captured_by: 'third_party',
      device_id: meta.deviceId,
      started_at: meta.startedAt,
      ended_at: meta.endedAt,
    },
    provenance_grade: 'declared',
    signatures: [],
  };
}

/* ----------------------------------------------------- token exchange */

export interface EvidenceGrant {
  token: string;
  serviceUrl: string;
}

/**
 * Exchange the EVAC session for a scoped evidence-service token (task §2). The
 * dashboard talks to EVAC with the user's session; the evidence service is a
 * separate origin with its own token, so ingest first swaps one for the other.
 *
 * Replicated from the client PR #224 owns — fold in when it lands.
 */
export async function grantEvidenceToken(orgId: string): Promise<EvidenceGrant> {
  const access = session.access;
  const res = await fetch(`${EVAC_BASE}/orgs/${orgId}/evidence/token`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(access ? { authorization: `Bearer ${access}` } : {}),
    },
    body: '{}',
  });
  if (!res.ok) {
    throw new CaptureError(
      'grant',
      `could not authorise evidence upload (${res.status})`,
      res.status,
    );
  }
  const body = (await res.json()) as EvidenceGrant;
  return body;
}

/* ------------------------------------------------------------- ingest */

async function serviceJson<T>(
  grant: EvidenceGrant,
  path: string,
  init: RequestInit,
  stage: CaptureStage,
): Promise<T> {
  const res = await fetch(`${grant.serviceUrl}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${grant.token}`,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  // Reason-for-access (spec 03-audit §… / 428). Unlikely on ingest, but if the
  // service ever demands a stated reason before it will open a session, say so
  // rather than failing opaquely.
  if (res.status === 428) {
    throw new CaptureError(
      'reason_required',
      'the evidence service requires a reason for access',
      428,
    );
  }
  if (!res.ok) {
    throw new CaptureError(stage, `${init.method ?? 'GET'} ${path} → ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export interface IngestSession {
  sessionId: string;
  parts: number[];
}

/** Open the ingest session with the manifest and its provenance (task §2). */
export async function openIngestSession(
  grant: EvidenceGrant,
  manifest: CaptureManifest,
  provenance: Provenance,
): Promise<IngestSession> {
  return serviceJson<IngestSession>(
    grant,
    '/v1/ingest/sessions',
    { method: 'POST', body: JSON.stringify({ manifest, provenance }) },
    'opening',
  );
}

export interface UploadedPart {
  idx: number;
  etag: string;
}

/**
 * Upload one segment: fetch its presigned URL, then PUT the bytes direct to
 * storage with `x-amz-checksum-sha256` so the store rejects on mismatch at the
 * door (spec §1.2). Bytes never transit the ingest API.
 */
export async function uploadSegment(
  grant: EvidenceGrant,
  sessionId: string,
  segment: CaptureSegment,
): Promise<UploadedPart> {
  const { url } = await serviceJson<{ url: string }>(
    grant,
    `/v1/ingest/sessions/${sessionId}/parts/${segment.index}/url`,
    { method: 'POST', body: '{}' },
    'uploading',
  );
  const put = await fetch(url, {
    method: 'PUT',
    body: segment.bytes as BodyInit,
    headers: { 'x-amz-checksum-sha256': hexToBase64(segment.sha256) },
  });
  if (!put.ok) {
    throw new CaptureError(
      'uploading',
      `part ${segment.index} upload failed (${put.status})`,
      put.status,
    );
  }
  return { idx: segment.index, etag: put.headers.get('etag') ?? `"web-${segment.index}"` };
}

export interface IngestReceipt {
  assetId: string;
  receipt: Record<string, string>;
  sig: Record<string, string>;
}

export async function completeIngest(
  grant: EvidenceGrant,
  sessionId: string,
  parts: UploadedPart[],
): Promise<IngestReceipt> {
  return serviceJson<IngestReceipt>(
    grant,
    `/v1/ingest/sessions/${sessionId}/complete`,
    { method: 'POST', body: JSON.stringify({ parts }) },
    'completing',
  );
}

/* -------------------------------------------------------- orchestrator */

export type CaptureProgress =
  | { phase: 'grant' }
  | { phase: 'opening' }
  | { phase: 'uploading'; done: number; total: number }
  | { phase: 'completing' }
  | { phase: 'done' };

export interface CapturePayload {
  manifest: CaptureManifest;
  provenance: Provenance;
  segments: CaptureSegment[];
}

/**
 * Drive the whole ingest: token exchange → open session → PUT every part →
 * complete → receipt (task §2). `onProgress` feeds the UI; any hop that throws
 * a `CaptureError` names the stage that failed so the screen can offer a retry.
 */
export async function ingestCapture(
  orgId: string,
  payload: CapturePayload,
  onProgress: (progress: CaptureProgress) => void,
): Promise<IngestReceipt> {
  onProgress({ phase: 'grant' });
  const grant = await grantEvidenceToken(orgId);

  onProgress({ phase: 'opening' });
  const opened = await openIngestSession(grant, payload.manifest, payload.provenance);

  // Honour the parts the session asked for (resume returns a subset); a fresh
  // session lists them all.
  const wanted = new Set(opened.parts);
  const toUpload = payload.segments.filter((s) => wanted.has(s.index));
  const total = toUpload.length;

  const parts: UploadedPart[] = [];
  for (const segment of toUpload) {
    onProgress({ phase: 'uploading', done: parts.length, total });
    parts.push(await uploadSegment(grant, opened.sessionId, segment));
  }
  onProgress({ phase: 'uploading', done: parts.length, total });

  onProgress({ phase: 'completing' });
  const receipt = await completeIngest(grant, opened.sessionId, parts);

  onProgress({ phase: 'done' });
  return receipt;
}
